/*
 * Notifications state store, signal-backed. Mirrors alarmsStore.ts:
 *  - Server pushes MentionNotifications over the SignalR `notifications`
 *    channel; the hub auto-joins `notifications-{userId}` on connect so we
 *    don't need an explicit subscribe.
 *  - Bootstrap seeds the signal from GET /api/me/notifications, which also
 *    carries the initial unread count so the bell badge lights up before the
 *    first push arrives.
 *  - After a reconnect we re-pull the snapshot (server does not replay).
 *  - Mark-seen is a writing action; the response drives the local state
 *    change so we don't optimistically mutate on optimistic-optimistically-
 *    optimistic failure paths.
 *
 * Kept module-scope because the bell + the /settings/identity mentions
 * section + a possible standalone /notifications route all share it.
 */

import { signal, computed, type Signal, type ReadonlySignal } from '@preact/signals';
import { api } from '../api/client';
import type { ListNotificationsResponse, NotificationDto } from '../api/types';
import { ensureHubStarted, onNotifications, onReconnected } from './hubClient';
import { sessionSignal } from '../auth/session';

/** Full inbox for the current user, newest first. Capped server-side at 100
 *  rows per request; the store keeps whatever we have, deduped by id. */
export const notificationsSignal = signal<NotificationDto[]>([]);

/** Server-authoritative unread count. Distinct from
 *  `notificationsSignal.filter(seenAt === null).length` because the server
 *  may have more unread rows than we've paged in. */
const unreadCountRawSignal = signal<number>(0);

/** Read-only view of the unread count — the bell badge feeds off this. */
export const unreadCountSignal: ReadonlySignal<number> = computed(() => unreadCountRawSignal.value);

let bootstrapPromise: Promise<void> | null = null;

/** Fetch the inbox snapshot and open the hub. Idempotent. Called once from
 *  App.tsx alongside bootstrapAlarms + bootstrapCommands. */
export function bootstrapNotifications(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    try {
      await ensureHubStarted();
    } catch (err) {
      // Non-fatal — the snapshot pull below still seeds the badge.
      // eslint-disable-next-line no-console
      console.warn('[notificationsStore] hub start failed, snapshot only', err);
    }
    await refreshSnapshot();
  })();
  return bootstrapPromise;
}

async function refreshSnapshot(): Promise<void> {
  try {
    const snap = await api.get<ListNotificationsResponse>('/api/me/notifications');
    // Snapshot replaces whatever we may have gathered during boot.
    // Race: if a push landed first, prefer the server view for the row set,
    // but keep any local push ids that the snapshot doesn't know about yet
    // (unlikely; strictly a defensive dedupe).
    const byId = new Map<string, NotificationDto>();
    for (const n of snap.notifications) byId.set(n.id, n);
    for (const n of notificationsSignal.value) if (!byId.has(n.id)) byId.set(n.id, n);
    notificationsSignal.value = Array.from(byId.values())
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    unreadCountRawSignal.value = snap.unreadCount;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[notificationsStore] snapshot fetch failed', err);
  }
}

// On every hub reconnect, re-pull the snapshot to catch anything we missed
// while offline. Same pattern as alarmsStore.
onReconnected(() => { void refreshSnapshot(); });

/** Apply a single pushed event to the signal.
 *
 *  Defensive on the wire: the SignalR JSON payload MUST carry a recipient id
 *  matching the current session. A misrouted push should never surface in
 *  someone else's bell (defence in depth — the server-side group filter is
 *  the real gate). */
function applyEvent(evt: NotificationDto): void {
  if (!evt || typeof evt.id !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('[notificationsStore] dropped malformed notification', evt);
    return;
  }
  const currentUser = sessionSignal.value.user?.userId ?? null;
  if (currentUser && evt.recipientUserId && evt.recipientUserId !== currentUser) {
    // eslint-disable-next-line no-console
    console.warn('[notificationsStore] dropped notification for other user',
      { theirs: evt.recipientUserId, mine: currentUser });
    return;
  }

  const list = notificationsSignal.value;
  const idx = list.findIndex((n) => n.id === evt.id);
  if (idx === -1) {
    notificationsSignal.value = [evt, ...list];
    if (!evt.seenAt) unreadCountRawSignal.value = unreadCountRawSignal.value + 1;
  } else {
    const next = list.slice();
    const prev = next[idx];
    next[idx] = evt;
    notificationsSignal.value = next;
    // Only adjust the unread count when the seen flag actually changed.
    if (!prev.seenAt && evt.seenAt) {
      unreadCountRawSignal.value = Math.max(0, unreadCountRawSignal.value - 1);
    } else if (prev.seenAt && !evt.seenAt) {
      unreadCountRawSignal.value = unreadCountRawSignal.value + 1;
    }
  }
}

onNotifications(applyEvent);

/** Mark a single notification seen (server + local). Idempotent. */
export async function markNotificationSeen(id: string): Promise<void> {
  try {
    await api.post<void>(`/api/me/notifications/${id}/seen`);
    const list = notificationsSignal.value;
    const idx = list.findIndex((n) => n.id === id);
    if (idx === -1) return;
    const prev = list[idx];
    if (prev.seenAt) return; // already seen locally
    const next = list.slice();
    next[idx] = { ...prev, seenAt: new Date().toISOString() };
    notificationsSignal.value = next;
    unreadCountRawSignal.value = Math.max(0, unreadCountRawSignal.value - 1);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[notificationsStore] markSeen failed', err);
  }
}

/** Mark every notification of the current user seen. */
export async function markAllNotificationsSeen(): Promise<void> {
  try {
    await api.post<{ affected: number }>('/api/me/notifications/mark-all-seen');
    const now = new Date().toISOString();
    notificationsSignal.value = notificationsSignal.value.map((n) =>
      n.seenAt ? n : { ...n, seenAt: now });
    unreadCountRawSignal.value = 0;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[notificationsStore] markAllSeen failed', err);
  }
}

/** Test/debug reset. */
export function __resetNotificationsStoreForTest(): void {
  notificationsSignal.value = [];
  unreadCountRawSignal.value = 0;
  bootstrapPromise = null;
}

export type { Signal };
